'use client';

/**
 * useVersionGuard — prompt the user to reload when an admin explicitly
 * signals a forced reload via POST /api/admin/force-reload.
 *
 * Trigger: `force_reload === true` in the /api/version response ONLY.
 * Hash drift alone no longer fires the toast — that was the 2026-06-03
 * false-positive bug where byte-identical-but-differently-tagged builds
 * (e.g., frontend ships ahead of backend on security-headers rollouts)
 * produced constant noise without any real incompatibility.
 *
 * Polled on mount (after a settling delay so we don't hit the network
 * on first paint), on tab focus, and every 5 minutes thereafter.
 *
 * De-duplicated across tabs via BroadcastChannel: the first tab to
 * detect force_reload signals every other tab, so the user gets one
 * toast total instead of N.
 *
 * Reload is the user's choice — never automatic. Closing or
 * suspending the toast must always be acceptable; an active chat
 * stream MUST never be interrupted by a forced reload.
 */
import { useEffect, useRef, useState } from 'react';
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 min
const INITIAL_DELAY_MS = 30 * 1000; // 30 s after mount
const CHANNEL_NAME = 'crucible-version';
interface VersionResponse {
  build_hash: string;
  force_reload?: boolean;
}
export function useVersionGuard(): {
  stale: boolean;
  serverHash: string | null;
} {
  const [stale, setStale] = useState(false);
  const [serverHash, setServerHash] = useState<string | null>(null);
  // Latch: once force_reload has triggered for this session, don't re-fire
  // on subsequent polls. The toast dismissal (sessionStorage) handles
  // per-hash dedup; this ref prevents the BroadcastChannel from re-announcing
  // after the user has already been notified this session.
  const firedRef = useRef(false);
  useEffect(() => {
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;

    // Receive a "stale" announcement from a sibling tab.
    const onMessage = (e: MessageEvent) => {
      if (cancelled) return;
      if (e.data?.type === 'stale' && typeof e.data.serverHash === 'string') {
        setServerHash(e.data.serverHash);
        setStale(true);
        firedRef.current = true;
      }
    };
    channel?.addEventListener('message', onMessage);
    async function check() {
      if (cancelled) return;
      // Once force_reload has already triggered this session, skip further
      // polls — the user has already seen (or dismissed) the toast.
      if (firedRef.current) return;
      try {
        // Hit the BFF, not the AI service directly: the BFF resolves the
        // session and forwards X-Crucible-User-Id so the backend can
        // tell us whether an admin has flagged THIS user for a forced
        // reload (force_reload=true).
        const resp = await fetch(`/api/version`, {
          cache: 'no-store',
          credentials: 'include'
        });
        if (!resp.ok) return; // transient — try again next interval
        const data: VersionResponse = await resp.json();
        if (cancelled) return;
        setServerHash(data.build_hash);
        // Only trigger on explicit admin signal — hash drift alone is not
        // enough. Byte-identical-but-differently-tagged builds (e.g. a
        // frontend security-headers rollout that ships before the backend)
        // used to fire false positives here. The force_reload flag is the
        // only authoritative "user must reload" signal.
        if (data.force_reload === true) {
          setStale(true);
          firedRef.current = true;
          channel?.postMessage({
            type: 'stale',
            serverHash: data.build_hash
          });
        }
        // Do NOT clear stale on false — once force_reload has fired, the
        // component's per-hash sessionStorage dismiss is the right escape
        // hatch, not a subsequent poll returning false.
      } catch {
        // Network blip — silent. The next poll catches it.
      }
    }
    const initialTimeout = setTimeout(check, INITIAL_DELAY_MS);
    // NOTE: no separate 'focus' listener. It used to sit here alongside the
    // visibilitychange handler below, and browsers fire both on a single tab
    // switch — so every foreground cost two /api/version calls, which is why
    // a 5-minute poll produced 2,236 requests in a week (AFD, 2026-08-06..13).
    // visibilitychange alone covers returning to the tab.
    // Skip the 5-min tick while the tab is backgrounded and catch up
    // immediately on return -- a dormant tab was polling at the same rate
    // as an active one, and with several Crucible tabs open (a normal
    // usage pattern) that's background request volume from a single
    // session even while nobody is looking at any of them. Confirmed live
    // 2026-07-21 as a plausible contributor to intermittent AFD/WAF blocks.
    intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') check();
    }, POLL_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      cancelled = true;
      clearTimeout(initialTimeout);
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      channel?.removeEventListener('message', onMessage);
      channel?.close();
    };
  }, []);
  return {
    stale,
    serverHash
  };
}